import React, { memo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate, formatLargeNumber, getChartColor, formatWeekTooltipLabel, getISOWeekDateRange, getISOWeekNumber } from '../../utils/analyticsHelpers';
import { computeRegionShares, isUsaGroupKey } from '../../utils/usaGrouping';
import { flattenPulseTooltip } from './pulseSkuTooltip';

const TOOLTIP_SCROLL_STEP = 96;

export const ChartTooltipFrame = memo(function ChartTooltipFrame({
  theme,
  children,
  className = '',
  style = {},
  scrollable = false,
}) {
  const bodyRef = useRef(null);

  const onScrollClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    bodyRef.current?.scrollBy({ top: TOOLTIP_SCROLL_STEP, behavior: 'smooth' });
  };

  const stopChart = (e) => {
    e.stopPropagation();
  };

  return (
    <div
      className={`w-max max-w-[min(92vw,36rem)] overflow-x-clip rounded-lg border shadow-lg ${scrollable ? 'flex max-h-[min(420px,70vh)] flex-col p-0' : 'p-3'} ${className}`}
      data-pulse-tooltip="true"
      style={{
        backgroundColor: theme.tooltipBg,
        borderColor: theme.tooltipBorder,
        pointerEvents: scrollable ? 'auto' : undefined,
        ...style,
      }}
      onMouseDown={scrollable ? stopChart : undefined}
      onClick={scrollable ? stopChart : undefined}
      onWheel={scrollable ? stopChart : undefined}
    >
      <div
        ref={scrollable ? bodyRef : undefined}
        className={scrollable ? 'min-h-0 flex-1 overflow-y-auto overscroll-contain p-3' : undefined}
      >
        {children}
      </div>
      {scrollable ? (
        <button
          type="button"
          aria-label="Scroll tooltip"
          className="flex w-full shrink-0 items-center justify-center pb-1 pt-0 text-xs leading-none"
          style={{ color: theme.tooltipSecondaryText }}
          onClick={onScrollClick}
        >
          ▾
        </button>
      ) : null}
    </div>
  );
});

function PulsePriceCells({ row, theme }) {
  if (row.plain != null && row.low == null && row.high == null) {
    return (
      <span className="shrink-0 tabular-nums" style={{ color: theme.tooltipText, fontWeight: 500 }}>
        ${Number(row.plain).toFixed(2)}
      </span>
    );
  }
  return <TooltipPrices low={row.low} high={row.high} lowColor={row.lowColor} highColor={row.highColor} theme={theme} />;
}

const PULSE_ROW = 'grid w-full grid-cols-[minmax(0,1fr)_auto] items-start gap-x-3 py-0 text-sm leading-tight';

function PulseTooltipBlock({ block, theme, depth }) {
  const rows = block.rows || [];
  const kids = block.blocks || [];
  const lone = rows.length === 1 && !rows[0].label;
  return (
    <div className={depth ? 'pl-2' : undefined}>
      {block.heading ? (
        <div className={`${PULSE_ROW} pl-2`}>
          <span
            className={`whitespace-nowrap ${block.strong ? 'font-semibold' : ''}`}
            style={{ color: theme.tooltipText }}
          >
            {block.heading}
          </span>
          {lone ? <PulsePriceCells row={rows[0]} theme={theme} /> : null}
        </div>
      ) : null}
      {(!block.heading || !lone ? rows : []).map((row, i) => (
        <div
          key={`${row.label}-${i}`}
          className={`${PULSE_ROW} pl-3`}
        >
          {row.label ? (
            <span className="whitespace-nowrap" style={{ color: theme.tooltipSecondaryText }}>{row.label}</span>
          ) : (
            <span />
          )}
          <PulsePriceCells row={row} theme={theme} />
        </div>
      ))}
      {kids.map((child, i) => (
        <PulseTooltipBlock key={`${child.heading}-${i}`} block={child} theme={theme} depth={depth + 1} />
      ))}
    </div>
  );
}

function TooltipPrices({ low, high, lowColor, highColor, theme }) {
  if (low == null && high == null) return null;
  return (
    <span className="grid w-[12.5rem] shrink-0 grid-cols-2 gap-x-2 text-sm tabular-nums leading-tight whitespace-nowrap">
      <span className="inline-flex min-h-[1.1em] items-center gap-1">
        {high != null ? (
          <>
            {highColor ? <ChartTooltipColorDot color={highColor} /> : null}
            <span style={{ color: theme.tooltipText }}>High ${Number(high).toFixed(2)}</span>
          </>
        ) : null}
      </span>
      <span className="inline-flex min-h-[1.1em] items-center gap-1">
        {low != null ? (
          <>
            {lowColor ? <ChartTooltipColorDot color={lowColor} /> : null}
            <span style={{ color: theme.tooltipText }}>Low ${Number(low).toFixed(2)}</span>
          </>
        ) : null}
      </span>
    </span>
  );
}

export const ChartTooltipColorDot = memo(function ChartTooltipColorDot({ color }) {
  return (
    <span
      className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
      style={{ backgroundColor: color }}
    />
  );
});

export const LinePriceTooltipBody = memo(function LinePriceTooltipBody({
  active,
  payload,
  label,
  theme,
  fontSize,
  commodity,
  /** When set, the weekly-volume series is broken out into its own subsection */
  volumeLineDataKey,
  /** Row key holding the weekly volume total on every row (the series key is anchored to one point per week) */
  volumeTooltipDataKey,
  volumeSeriesColor,
}) {
  const { t, i18n } = useTranslation();

  if (!active || !payload?.length) return null;

  // The chart passes filterNull={false} so the row stays readable even when every
  // series is null on a hovered day; hide null entries here instead. The volume
  // series renders in its own subsection, never as a price row.
  const visibleEntries = payload.filter(
    (entry) => entry.value != null && entry.dataKey !== volumeLineDataKey
  );

  const rawDate = payload[0]?.payload?.rawDate;
  const hoveredDate = rawDate instanceof Date && !Number.isNaN(rawDate.getTime()) ? rawDate : null;

  // Weekly-volume subsection: value from the anchored series entry when present,
  // otherwise from the per-row weekly total carried on every row.
  const volumeSection = (() => {
    if (volumeLineDataKey == null) return null;
    const entry = payload.find((e) => e.dataKey === volumeLineDataKey && e.value != null);
    const raw = entry != null ? entry.value : payload[0]?.payload?.[volumeTooltipDataKey];
    if (raw == null) return null;
    const value = Number(raw);
    if (!Number.isFinite(value)) return null;
    if (!hoveredDate) return { value, week: null, rangeText: null };
    const week = getISOWeekNumber(hoveredDate);
    const month = hoveredDate.getMonth();
    let isoYear = hoveredDate.getFullYear();
    if (week === 1 && month === 11) isoYear += 1;
    if (week >= 52 && month === 0) isoYear -= 1;
    const range = getISOWeekDateRange(week, isoYear);
    let rangeText = null;
    if (range) {
      try {
        // Locale-aware, compacts same-month ranges ("Mar 23–29") and formats
        // cross-month/year weeks in full ("Dec 29 – Jan 4").
        rangeText = new Intl.DateTimeFormat(i18n.language, { month: 'short', day: 'numeric' })
          .formatRange(range.start, range.end);
      } catch {
        rangeText = null;
      }
    }
    return { value, week, rangeText };
  })();

  if (!visibleEntries.length && volumeSection == null) return null;

  const formattedLabel = (() => {
    if (hoveredDate) return formatDate(hoveredDate);
    if (label == null) return '';
    const s = String(label);
    return s.includes('NaN') || s.includes('undefined') ? '' : s;
  })();

  return (
    <ChartTooltipFrame theme={theme} scrollable>
      <p className="mb-2 font-medium" style={{ color: theme.tooltipText, fontSize }}>
        {formattedLabel}
      </p>
      {volumeSection != null && (
        <div className="mb-2 pb-2" style={{ borderBottom: `1px solid ${theme.tooltipBorder}` }}>
          <div className="flex items-center gap-2 py-0.5 text-sm">
            <ChartTooltipColorDot color={volumeSeriesColor} />
            <span className="text-[0.85em]" style={{ color: theme.tooltipSecondaryText }}>
              {volumeSection.week != null
                ? t('charts.pricePulse.weekTotalVolume', { week: volumeSection.week })
                : t('charts.pricePulse.volumeSeriesName')}
              :
            </span>
            <span style={{ color: theme.tooltipText, fontWeight: 500 }}>
              {`${formatLargeNumber(volumeSection.value)} lbs`}
            </span>
          </div>
          <p className="pl-[18px] text-xs" style={{ color: theme.tooltipSecondaryText }}>
            {t('charts.pricePulse.volumeSubtext')}
            {volumeSection.rangeText ? ` · ${volumeSection.rangeText}` : ''}
          </p>
        </div>
      )}
      {flattenPulseTooltip(visibleEntries, commodity).map((district) => (
        <div key={district.key} className="mb-1 last:mb-0">
          {district.label ? (
          <p className="font-semibold leading-tight" style={{ color: theme.tooltipText, fontSize }}>
            {district.label}
          </p>
          ) : null}
          {(district.blocks || []).map((block, i) => (
            <PulseTooltipBlock key={`${block.heading}-${i}`} block={block} theme={theme} depth={0} />
          ))}
          {district.more > 0 ? (
            <p className="pl-2 text-sm" style={{ color: theme.tooltipSecondaryText }}>+{district.more} more</p>
          ) : null}
        </div>
      ))}
    </ChartTooltipFrame>
  );
});

export const FobAvgYearTooltipBody = memo(function FobAvgYearTooltipBody({
  active,
  payload,
  label,
  theme,
  fontSize,
}) {
  if (!active || !payload?.length) return null;

  return (
    <ChartTooltipFrame theme={theme}>
      {label != null && String(label).length > 0 && (
        <p className="mb-2 font-medium" style={{ color: theme.tooltipText, fontSize }}>
          {label}
        </p>
      )}
      {payload.map((entry, i) => (
        <div key={i} className="flex items-center gap-2 py-0.5 text-sm">
          <ChartTooltipColorDot color={entry.color} />
          <span className="text-[0.85em]" style={{ color: theme.tooltipSecondaryText }}>
            {entry.name}:
          </span>
          <span style={{ color: theme.tooltipText, fontWeight: 500 }}>
            ${entry.value != null ? Number(entry.value).toFixed(2) : '—'}
          </span>
        </div>
      ))}
    </ChartTooltipFrame>
  );
});

export const FobByOriginTooltipBody = memo(function FobByOriginTooltipBody({
  active,
  payload,
  label,
  theme,
  fontSize,
  originsWithData,
  granularity,
  selectedYear,
  avgPriceLabel,
}) {
  if (!active || !payload?.length) return null;

  const dataPoint = payload[0]?.payload;
  if (!dataPoint) return null;

  const originPrices = originsWithData
    .map((origin) => ({
      origin,
      price: dataPoint[origin],
      color: getChartColor(originsWithData.indexOf(origin), originsWithData.length),
    }))
    .filter((item) => item.price !== null && item.price !== undefined)
    .sort((a, b) => b.price - a.price);

  if (originPrices.length === 0) return null;

  const periodLabel = granularity === 'weekly' ? `Week ${String(label || '').replace('W', '')}` : label;

  return (
    <ChartTooltipFrame theme={theme}>
      <div className="mb-2 font-medium" style={{ color: theme.tooltipText, fontSize }}>
        {periodLabel} {selectedYear || ''} - {avgPriceLabel}
      </div>
      {originPrices.map(({ origin, price, color }) => (
        <div key={origin} className="mb-1 flex items-center gap-2 last:mb-0">
          <ChartTooltipColorDot color={color} />
          <span style={{ color: theme.tooltipSecondaryText }}>{origin}:</span>
          <span style={{ color: theme.tooltipText, fontWeight: 500 }}>${price?.toFixed(2)}</span>
        </div>
      ))}
    </ChartTooltipFrame>
  );
});

function yearFromDistrictKey(district) {
  const m = String(district).match(/^(\d{4})_/);
  return m ? parseInt(m[1], 10) : null;
}

function districtLabelFromKey(district) {
  if (district == null) return '';
  const s = String(district);
  const m = s.match(/^(\d{4})_(.+)$/);
  if (m) return `${m[2]} (${m[1]})`;
  return s.replace(/^\d{4}_/, '');
}

export const VolumeByDistrictTooltipBody = memo(function VolumeByDistrictTooltipBody({
  active,
  payload,
  label,
  theme,
  fontSize,
  selectedYKeys,
  yKeys,
  chartColors,
  viewMode,
}) {
  if (!active || !payload?.length) return null;

  const dataPoint = payload[0]?.payload;
  if (!dataPoint) return null;

  const rawItems = selectedYKeys
    .map((key) => ({
      key,
      value: dataPoint[key],
      color: chartColors[yKeys.indexOf(key)],
      year: yearFromDistrictKey(key),
    }))
    .filter((item) => item.value != null && item.value > 0);

  if (rawItems.length === 0) return null;

  // Paired format: rank regions by current-year volume DESC, then interleave years
  const maxYear = rawItems.reduce((m, i) => Math.max(m, i.year || 0), 0);
  const regionGroups = new Map();
  rawItems.forEach((item) => {
    const m = String(item.key).match(/^\d{4}_(.+)$/);
    const region = m ? m[1] : item.key;
    if (!regionGroups.has(region)) regionGroups.set(region, []);
    regionGroups.get(region).push(item);
  });
  const items = [...regionGroups.entries()]
    .map(([, entries]) => ({
      entries: entries.sort((a, b) => (b.year || 0) - (a.year || 0)),
      currentVol: entries.find((e) => e.year === maxYear)?.value || 0,
    }))
    .sort((a, b) => b.currentVol - a.currentVol)
    .flatMap(({ entries }) => entries);

  let displayLabel = label;
  if (viewMode === 'weekly' && label) {
    const weekNum = parseInt(label, 10);
    if (!Number.isNaN(weekNum)) {
      const year = items[0]?.year ?? new Date().getFullYear();
      displayLabel = formatWeekTooltipLabel(weekNum, year);
    }
  }

  return (
    <ChartTooltipFrame theme={theme}>
      <div className="mb-2 font-medium" style={{ color: theme.tooltipText, fontSize }}>
        {displayLabel}
      </div>
      {items.map(({ key, value, color, year }) => {
        // Grouped USA series: total row, then each region's share of that total.
        const breakdown = isUsaGroupKey(key) ? dataPoint.usaBreakdown?.[year] : null;
        const shares = breakdown ? computeRegionShares(breakdown).regions : [];
        return (
          <React.Fragment key={key}>
            <div className="mb-1 flex items-center gap-2 last:mb-0">
              <ChartTooltipColorDot color={color} />
              <span style={{ color: theme.tooltipSecondaryText }}>{districtLabelFromKey(key)}:</span>
              <span style={{ color: theme.tooltipText, fontWeight: 500 }}>{formatLargeNumber(value)}</span>
            </div>
            {shares.map(({ region, value: regionValue, percent }) => (
              <div key={`${key}__${region}`} className="mb-1 flex items-center gap-2 pl-5 last:mb-0">
                <span style={{ color: theme.tooltipSecondaryText }}>{region}:</span>
                <span style={{ color: theme.tooltipText, fontWeight: 500 }}>
                  {formatLargeNumber(regionValue)} ({percent.toFixed(1)}%)
                </span>
              </div>
            ))}
          </React.Fragment>
        );
      })}
    </ChartTooltipFrame>
  );
});

export const MarketShareTooltipBody = memo(function MarketShareTooltipBody({
  active,
  payload,
  label,
  theme,
  fontSize,
}) {
  if (!active || !payload?.length) return null;

  const entries = payload
    .map((p) => ({
      name: p.name,
      value: p.value,
      color: p.fill,
    }))
    .filter((e) => e.value > 0.001)
    .sort((a, b) => b.value - a.value);

  if (entries.length === 0) return null;

  return (
    <ChartTooltipFrame theme={theme}>
      <p className="mb-2 font-medium" style={{ color: theme.tooltipText, fontSize }}>
        {label}
      </p>
      {entries.map((entry, idx) => (
        <div key={idx} className="flex items-center gap-2 py-1">
          <ChartTooltipColorDot color={entry.color} />
          <span style={{ fontSize: fontSize - 2, color: theme.tooltipSecondaryText }}>
            {entry.name}: {(entry.value * 100).toFixed(1)}%
          </span>
        </div>
      ))}
    </ChartTooltipFrame>
  );
});
